import CustomPagination from '@/platform/common/components/CustomPagination/CustomPagination';
import EmployeeCard from '@/platform/common/components/EmployeeCard/EmployeeCard';
import NoDataRow from '@/platform/common/components/NoDataRow/NoDataRow';
import SearchBarFilter from '@/platform/common/components/SearchBarFilter/SearchBarFilter';
import { useFilters } from '@/platform/hooks/useFilters';
import { formatMoney, localDate } from '@/platform/lib/utils';
import {
    Button,
    Container,
    FilterBarDropdown,
    Segment,
    SegmentContent,
    SegmentFooter,
    SegmentHeader,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCell,
    TableRow,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@/platform/ui';
import {
    CheckIcon,
    ClockIcon,
    ExclamationCircleIcon,
    FlagIcon,
    XMarkIcon,
} from '@heroicons/react/20/solid';
import { router, usePage } from '@inertiajs/react';
import dayjs from 'dayjs';
import { useState } from 'react';

const yearOptions = Array.from(
    { length: dayjs().year() - 2018 + 1 },
    (_, index) => {
        const year = dayjs().year() - index;
        return { id: year, name: String(year) };
    }
);

const statusOptions = [
    { id: 'open', name: 'Openstaande inventarisaties' },
    {
        id: 'open-ex-afgerond',
        name: 'Openstaande inventarisaties zonder afgerond',
    },
    { id: 'excluded', name: 'Uitgesloten inventarisaties' },
    { id: 'result', name: 'Resultaat' },
];

const icons = {
    done: { Icon: CheckIcon, className: 'text-green-600', label: 'Afgerond' },
    pending: {
        Icon: ClockIcon,
        className: 'text-orange-500',
        label: 'In behandeling',
    },
    overdue: {
        Icon: ClockIcon,
        className: 'text-red-600',
        label: 'Staat > 1 maand uit',
    },
    none: {
        Icon: XMarkIcon,
        className: 'text-gray-400',
        label: 'Geen informatie',
    },
    declined: {
        Icon: ExclamationCircleIcon,
        className: 'text-red-600',
        label: 'Afgewezen',
    },
    final: {
        Icon: XMarkIcon,
        className: 'text-red-600',
        label: 'Definitief afgewezen',
    },
    missing: {
        Icon: FlagIcon,
        className: 'text-red-600',
        label: 'Doelgroepverklaring ontbreekt',
    },
};

const FINAL_DECLINED_STATUSES = [9, 14, 15, 16, 17];

const columns = [
    {
        abbr: 'I',
        label: 'Inventarisatie',
        state: view => {
            if (!view.form_created_at) return 'none';
            if (view.is_form_complete) return 'done';

            return dayjs(view.form_created_at).isBefore(
                dayjs().subtract(1, 'month')
            )
                ? 'overdue'
                : 'pending';
        },
    },
    {
        abbr: 'M',
        label: 'Machtiging',
        state: view => {
            if (view.machtiging) return 'done';
            if ([2, 3, 4, 19].includes(view.form_status_id)) return 'pending';
        },
    },
    {
        abbr: 'V',
        label: 'Verstuurd',
        state: view => {
            if (
                view.ontvangstbevestiging ||
                view.dgv ||
                [5, 12].includes(view.form_status_id)
            ) {
                return 'done';
            }
            if (view.form_status_id === 6) return 'final';
        },
    },
    {
        abbr: 'O',
        label: 'Ontvangstbevestiging',
        state: view => {
            if (view.ontvangstbevestiging) return 'done';
            if ([5, 12].includes(view.form_status_id)) return 'pending';
        },
    },
    {
        abbr: 'D',
        label: 'Doelgroepverklaring',
        state: view => {
            if (view.dgv) return 'done';
            if ([5, 12].includes(view.form_status_id)) return 'pending';
            if (view.form_status_id === 6) return 'declined';
            if (view.form_status_id === 7) return 'missing';
        },
    },
];

const StatusIcon = ({ column, view }) => {
    const state =
        column.state(view) ??
        (FINAL_DECLINED_STATUSES.includes(view.form_status_id)
            ? 'final'
            : null);

    if (!state) {
        return null;
    }

    const { Icon, className, label } = icons[state];

    return (
        <Tooltip>
            <TooltipTrigger>
                <Icon className={`mx-auto size-5 ${className}`} />
            </TooltipTrigger>
            <TooltipContent>{`${column.label}: ${label}`}</TooltipContent>
        </Tooltip>
    );
};

const EmploymentsTooltip = ({ view }) => (
    <Tooltip>
        <TooltipTrigger>{localDate(view.employment?.starts_at)}</TooltipTrigger>
        <TooltipContent>
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHeaderCell colSpan={3}>
                            Dienstverbanden
                        </TableHeaderCell>
                    </TableRow>
                    <TableRow>
                        <TableHeaderCell>Pers.nr.</TableHeaderCell>
                        <TableHeaderCell>Ind.</TableHeaderCell>
                        <TableHeaderCell textAlign='right'>
                            Uit.
                        </TableHeaderCell>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {view.employee?.employments?.map(employment => (
                        <TableRow key={employment.id}>
                            <TableCell>{employment.staff_number}</TableCell>
                            <TableCell>
                                {localDate(employment.starts_at)}
                            </TableCell>
                            <TableCell textAlign='right'>
                                {localDate(employment.ends_at)}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TooltipContent>
    </Tooltip>
);

const CallListView = () => {
    const { auth, views, total_prognose, target_groups } = usePage().props;
    const { filters, updateFilters, isSorted } = useFilters([
        'views',
        'total_prognose',
    ]);
    const [exporting, setExporting] = useState(false);

    const showEntity = filters?.entity_ids?.length > 1;
    const numberOfColumns = 5 + columns.length + (showEntity ? 1 : 0);

    const handleExport = () =>
        router.post('/services/lkv/calllist/export', filters, {
            preserveScroll: true,
            preserveState: true,
            onStart: () => setExporting(true),
            onFinish: () => setExporting(false),
        });

    return (
        <Container fluid>
            <Segment>
                <SegmentHeader
                    header={`Loonkostenvoordeel (${views?.meta?.total || 0}) (${formatMoney(total_prognose || 0)})`}
                >
                    <Button onClick={handleExport} loading={exporting}>
                        Exporteren
                    </Button>
                </SegmentHeader>
                <SegmentContent>
                    <SearchBarFilter only={['views', 'total_prognose']}>
                        <FilterBarDropdown
                            multiple
                            selectAll
                            label={`Entiteiten (${filters?.entity_ids?.length || 0}/${auth?.customer?.entities?.length || 0})`}
                            options={auth?.customer?.entities?.map(entity => ({
                                id: entity.id,
                                name: entity.name,
                            }))}
                            value={filters?.entity_ids || []}
                            onChange={value =>
                                updateFilters('entity_ids', value)
                            }
                        />
                        <FilterBarDropdown
                            clearable
                            label={filters?.year || 'Alle jaren'}
                            options={yearOptions}
                            value={filters?.year || ''}
                            onChange={value => updateFilters('year', value)}
                        />
                        <FilterBarDropdown
                            multiple
                            label={`Doelgroepen (${filters?.target_group_ids?.length || 0}/${Object.keys(target_groups || {}).length})`}
                            options={Object.entries(target_groups || {}).map(
                                ([id, name]) => ({ id: Number(id), name })
                            )}
                            value={filters?.target_group_ids || []}
                            onChange={value =>
                                updateFilters('target_group_ids', value)
                            }
                        />
                        <FilterBarDropdown
                            clearable
                            label={
                                statusOptions.find(
                                    option => option.id === filters?.status
                                )?.name ?? 'Toon iedereen'
                            }
                            options={statusOptions}
                            value={filters?.status || ''}
                            onChange={value => updateFilters('status', value)}
                        />
                    </SearchBarFilter>

                    <Table striped>
                        <TableHeader>
                            <TableRow>
                                <TableHeaderCell>Medewerker</TableHeaderCell>
                                {showEntity && (
                                    <TableHeaderCell>Entiteit</TableHeaderCell>
                                )}
                                <TableHeaderCell>
                                    Datum in dienst
                                </TableHeaderCell>
                                <TableHeaderCell>
                                    Datum uit dienst
                                </TableHeaderCell>
                                <TableHeaderCell>Doelgroep</TableHeaderCell>
                                <TableHeaderCell
                                    textAlign='right'
                                    sorted={isSorted('prognose')}
                                    onClick={() =>
                                        updateFilters('sort', 'prognose')
                                    }
                                >
                                    Resultaat
                                </TableHeaderCell>
                                {columns.map(column => (
                                    <TableHeaderCell
                                        key={column.abbr}
                                        textAlign='center'
                                        collapsing
                                        title={column.label}
                                    >
                                        {column.abbr}
                                    </TableHeaderCell>
                                ))}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {views?.meta?.total === 0 && (
                                <NoDataRow numberOfColumns={numberOfColumns} />
                            )}

                            {views?.data?.map(view => (
                                <TableRow
                                    key={view.id}
                                    error={view.excluded || view.score === 3}
                                    className={
                                        view.score === 1
                                            ? '!bg-green-50'
                                            : undefined
                                    }
                                >
                                    <TableCell>
                                        <EmployeeCard
                                            employee={view.employee}
                                            link
                                        />
                                    </TableCell>
                                    {showEntity && (
                                        <TableCell>
                                            {view.entity?.name}
                                        </TableCell>
                                    )}
                                    <TableCell>
                                        <EmploymentsTooltip view={view} />
                                    </TableCell>
                                    <TableCell>
                                        {localDate(view.employment?.ends_at)}
                                    </TableCell>
                                    <TableCell>{view.target_group}</TableCell>
                                    <TableCell textAlign='right'>
                                        {formatMoney(view.prognose)}
                                        {filters?.year && (
                                            <div className='text-xs text-gray-500'>
                                                (
                                                {formatMoney(
                                                    view.total_prognose
                                                )}
                                                )
                                            </div>
                                        )}
                                    </TableCell>
                                    {columns.map(column => (
                                        <TableCell
                                            key={column.abbr}
                                            textAlign='center'
                                        >
                                            <StatusIcon
                                                column={column}
                                                view={view}
                                            />
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </SegmentContent>

                <SegmentFooter>
                    <CustomPagination
                        meta={views?.meta}
                        only={['views', 'total_prognose']}
                    />
                </SegmentFooter>
            </Segment>
        </Container>
    );
};

export default CallListView;
